import { ROUTES } from 'constants/routes';
import { ConsentAgreementKind, ConsentAgreementType } from 'constants/common';

// Consent registration sub-steps, in wizard order, mapped to their route.
export const CONSENT_STEP_ROUTE_ORDER: Array<{ type: ConsentAgreementType; route: string }> = [
  { type: ConsentAgreementType.TermsAndConditions, route: ROUTES.TERMS_AND_CONDITIONS.path },
  { type: ConsentAgreementType.ProjectConsent, route: ROUTES.STANDARD_CONSENT_FORM.path },
  { type: ConsentAgreementType.BiometricConsent, route: ROUTES.STANDARD_BIOMETRIC_CONSENT.path },
];

// Full ordered sequence of the consent sub-step routes (documents + signature).
// Used to enforce that a worker cannot skip ahead to a later step before
// completing the earlier ones.
export const CONSENT_ROUTE_SEQUENCE: string[] = [
  ...CONSENT_STEP_ROUTE_ORDER.map(s => s.route),
  ROUTES.REGISTRATION_SIGNATURE.path,
];

/**
 * Content ids of the STANDARD registration agreements this worker has accepted, for UI/routing.
 * Unions the server-confirmed set with the not-yet-uploaded buffer.
 *
 * The `temp.*` queue is deliberately NOT folded into the confirmed array: the upload handlers
 * subtract the confirmed set from what they are about to send, so promoting an id to "confirmed"
 * before the API call would make the uploader skip it and silently drop the acceptance.
 *
 * Never derive this from `cache.consentAgreements[].isAccepted` — that array is global and its flags
 * belong to whichever worker was fetched last.
 */
export function getAcceptedStandardConsentIds(onboarding: any): Set<string> {
  return new Set<string>([
    ...(onboarding?.acceptedStandardConsentIds || []),
    ...(onboarding?.temp?.consentAcceptances || []),
  ]);
}

/**
 * Content ids of the CUSTOM project consent forms this worker has accepted, for UI purposes.
 * Same confirmed-vs-queued split as `getAcceptedStandardConsentIds`.
 */
export function getAcceptedCustomConsentIds(onboarding: any): Set<string> {
  return new Set<string>([
    ...(onboarding?.acceptedCustomConsentIds || []),
    ...(onboarding?.temp?.customConsentAcceptances?.consentAgreementContentIds || []),
  ]);
}

/**
 * Returns the route of the consent sub-step the worker still needs to complete, in
 * order Terms → Standard Consent → Biometric → Signature. The whole flow is
 * considered complete once `onboarding.signatureUrl` has a value, so this returns
 * null in that case. It also returns null when no agreements are loaded yet
 * (cannot determine — don't force a redirect).
 *
 * A document type counts as accepted if any of its (possibly per-language) rows is in this worker's
 * accepted-id set — see `getAcceptedConsentAgreementIds`. This works identically online and offline
 * because both inputs are per-worker and independently valid.
 */
export function getFirstIncompleteConsentRoute(consentAgreements: any[], onboarding: any): string | null {
  // Signature captured → consent registration is complete.
  if (onboarding?.signatureUrl) return null;

  const agreements = consentAgreements || [];
  if (!agreements.length) return null;
  const acceptedIds = getAcceptedStandardConsentIds(onboarding);
  for (const { type, route } of CONSENT_STEP_ROUTE_ORDER) {
    const typeAgreements = agreements.filter(
      (a: any) => a.kind === ConsentAgreementKind.Standard && a.type === type,
    );
    if (!typeAgreements.length) continue;
    const isTypeAccepted = typeAgreements.some((a: any) => acceptedIds.has(a.consentAgreementContentId));
    if (!isTypeAccepted) return route;
  }
  // All documents acknowledged but no signature yet → the Signature step.
  return ROUTES.REGISTRATION_SIGNATURE.path;
}
